import { useCalcNotificationHeight } from '@/hooks/useCalcNotificationHeight';
import usePersistFn from '@/hooks/usePersistFn';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useNavigation } from '@remix-run/react';
import clsx from 'clsx';
import React from 'react';

export type ProgressBarProps = {
  className?: string;
};

export function ProgressBar(props: ProgressBarProps) {
  const { className } = props;

  const [progress, setProgress] = React.useState(0);
  const rafId = React.useRef(0);
  const notificationHeight = useCalcNotificationHeight();
  const setProgressBarLoading = useAppearanceConfigModelStore((x) => x.setProgressBarLoading);
  const progressBarLoading = useAppearanceConfigModelStore((x) => x.progressBarLoading);
  const hasLoadingRef = React.useRef(false);

  const updateProgress = usePersistFn(() => {
    setProgress((prevProgress) => {
      if (prevProgress >= 0.95 || !progressBarLoading) {
        cancelAnimationFrame(rafId.current);
        if (!progressBarLoading) {
          return 1;
        }
        return prevProgress;
      }
      return Math.min(prevProgress + 0.01, 0.95);
    });
    rafId.current = requestAnimationFrame(updateProgress);
  });

  React.useEffect(() => {
    if (progressBarLoading) {
      setProgress(0);
    }
    rafId.current = requestAnimationFrame(updateProgress);
    return () => {
      cancelAnimationFrame(rafId.current);
    };
  }, [progressBarLoading, updateProgress]);

  const navigation = useNavigation();
  React.useEffect(() => {
    if (navigation.state === 'idle') {
      setProgressBarLoading(false);
    } else {
      hasLoadingRef.current = true;
      setProgressBarLoading(true);
    }
  }, [navigation.state, setProgressBarLoading]);

  if (!hasLoadingRef.current) {
    return null;
  }
  return (
    <div
      className={clsx('absolute left-0 top-0 z-[1000] h-[2px] w-[100vw]', className)}
      style={{
        mixBlendMode: `${notificationHeight ? 'plus-lighter' : 'normal'}`,
      }}
    >
      <div
        style={{
          width: `${progress * 100}%`,
          height: progress === 1 ? '0px' : '2px',
          transition: progress === 1 ? 'height 0.3s cubic-bezier(1, 0, 1, 0) 0s' : 'none',
          boxShadow: `${
            notificationHeight ? 'none' : `0px 2px 8px 0px rgba(var(--ui-primary-color-p),0.6)`
          }`,
        }}
        className="h-[2px] bg-primary"
      />
    </div>
  );
}
